import "./RatingReasonCard.css";
import { Button } from "@ui/Button";
import { Card } from "@ui/Card";
import { Loader } from "@ui/Loader";
import { Stack } from "@ui/Stack";
import { createSignal, onMount, Show } from "solid-js";
import { getScrollParent } from "~/modules/shared/utils/scrollParent";

/** Breathing space left between the card and whatever edge was hiding it. */
const REVEAL_GAP_PX = 8;

/**
 * Bring the card into view with the smallest scroll that does the job. The
 * browser's own focus scrolling centres the card, which is far more movement
 * than this warrants, so callers focus with `preventScroll`.
 */
function revealCard(card: HTMLElement) {
	const scroller = getScrollParent(card);
	const port = scroller?.getBoundingClientRect();
	// The chat header floats over the top of the scrollport in some layouts.
	const header = document
		.querySelector(".chat-layout-header")
		?.getBoundingClientRect();
	const top = Math.max(port?.top ?? 0, header?.bottom ?? 0);
	const bottom = port?.bottom ?? window.innerHeight;

	const rect = card.getBoundingClientRect();
	const hiddenAbove = top + REVEAL_GAP_PX - rect.top;
	const hiddenBelow = rect.bottom + REVEAL_GAP_PX - bottom;
	// Favour the top edge: a card taller than the gap can't clear both.
	const distance = hiddenAbove > 0 ? -hiddenAbove : Math.max(0, hiddenBelow);
	if (distance === 0) {
		return;
	}
	(scroller ?? window).scrollBy({ top: distance, behavior: "smooth" });
}

type Props = {
	/** What is being rated, determiner included: "this answer", "these clips". */
	subject: string;
	/** Reason already stored against the rating, offered for editing. */
	reason: string;
	/** Keeps the card in view behind a waiting indicator while the reason is sent. */
	isSubmitting: boolean;
	onSubmit: (reason: string) => void;
	onDismiss: () => void;
};

/**
 * Prompt for the reason behind a negative rating. The rating itself is already
 * recorded by the time this appears, so dismissing costs nothing. Escape
 * dismissal is handled by the toolbar, which owns whether this is shown.
 */
export const RatingReasonCard = (props: Props) => {
	// Seeded once: the card is mounted afresh each time it opens, and later
	// updates from the toolbar must not overwrite what is being typed.
	const [reason, setReason] = createSignal(props.reason);
	let inputRef: HTMLInputElement | undefined;

	onMount(() => {
		inputRef?.focus({ preventScroll: true });
		// Caret after any reason carried over, ready to add to it.
		inputRef?.setSelectionRange(inputRef.value.length, inputRef.value.length);
		const card = inputRef?.closest<HTMLElement>(".rating-reason-card");
		if (!card) {
			return;
		}
		revealCard(card);
	});

	const submit = () => {
		if (props.isSubmitting) {
			return;
		}
		const value = reason().trim();
		if (value === "") {
			props.onDismiss();
			return;
		}
		props.onSubmit(value);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			submit();
		}
	};

	const prompt = () => `What was wrong with ${props.subject}?`;

	return (
		<Card class="rating-reason-card" theme="dark" borderRadius="standard">
			<Stack direction="row" spacing="medium" align="center">
				<input
					ref={inputRef}
					class="rating-reason-input"
					type="text"
					placeholder={prompt()}
					aria-label={prompt()}
					value={reason()}
					disabled={props.isSubmitting}
					onInput={(event) => setReason(event.currentTarget.value)}
					onKeyDown={handleKeyDown}
				/>
				<Show
					when={!props.isSubmitting}
					fallback={
						<Button theme="mint-grad" disabled>
							<Loader size="xsmall" color="var(--white)" />
						</Button>
					}
				>
					<Button
						symbol="check"
						symbolColor="white"
						symbolRelativeSize="even-larger"
						aria-label="Send reason"
						onClick={submit}
					/>
				</Show>
				<Button
					theme="grey"
					variant="secondary"
					symbol="close"
					symbolRelativeSize="even-larger"
					aria-label="Dismiss"
					disabled={props.isSubmitting}
					onClick={() => props.onDismiss()}
				/>
			</Stack>
		</Card>
	);
};
